import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {useDispatch} from 'react-redux';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import NoDataLabel from '../../app/components/layout/NoDataLabel';
import {toastHelper} from '../../utils/toast-helper';
import {synthesesActions} from '../../modules/syntheses/syntheses.actions';
import {useGetTerritoireQuery} from '../api.js';
import {fromQuery, toQuery, etatParDefaut} from '../permalink.js';
import {toCsv, toGeoJson} from '../export.js';
import {indexer} from '../selectors/index.js';
import {ecrireCollecte, useCollecte} from '../collecte.js';
import {etatsDesPav} from '../selectors/etat.js';
import {filtrerPavs} from '../selectors/filtres.js';
import {kpi} from '../selectors/kpi.js';
import {repartitions} from '../selectors/repartitions.js';
import {ACTE_LIBELLES, VERDICTS, VERDICT_LIBELLES} from '../tokens.js';
import Coquille from '../components/Coquille.js';
import CarteBarreFiltres from '../components/carte/CarteBarreFiltres.js';
import CartePanneau from '../components/carte/CartePanneau.js';
import CartePopup, {contenuPassage} from '../components/carte/CartePopup.js';
import {dernierPassage} from '../components/fiche/fiche.selectors.js';
import CarteLeaflet, {PopupPav} from '../components/carte/CarteLeaflet.js';
import CarteCalques from '../components/carte/CarteCalques.js';
import CarteCurseur from '../components/carte/CarteCurseur.js';
import {LegendeChalandise, LegendeDensite} from '../components/carte/CarteDensiteLayer.js';
import CarteLegendeCauses from '../components/carte/CarteLegendeCauses.js';
import {causeDominanteParPav, libelleCause} from '../selectors/variation.js';
import {RAYONS, RAYON_DEFAUT, libelleDistance, marquagesCarreaux, seuilsQuantiles} from '../selectors/population.js';
import {EncartChargement, EncartVide} from '../components/carte/CarteEtats.js';
import {telecharger} from '../components/carte/CarteUi.js';
import {enveloppesCircuits} from '../selectors/circuits.js';
import {bornerDate, bornesCurseur, derniereCollecte, partsParCommune} from '../components/carte/carte.selectors.js';

// Écran 2 du portail Pilotage : la Carte master (PIL-01 lot 4). L'état complet de la vue vit dans la query string
// (permalien) ; aucun slice Redux. Voir l'en-tête de selectors/index.js pour ce qui est mémoïsé.

const aujourdhui = () => new Date().toLocaleDateString('sv-SE'); // Y-m-d, fuseau local

function compterVerdicts(pavs, etats) {
    const res = {ras: 0, pb: 0, imp: 0, ni: 0};
    for (const p of pavs) res[etats.get(p.id).verdict]++;
    return res;
}

const MESURES = {};
const mesurerDessin = (ms) => { MESURES.dessinMs = ms; }; // référence stable : ne relance pas le dessin à chaque rendu
if (import.meta.env.DEV && typeof window !== 'undefined') window.__pilotageCarte = MESURES; // temps de calcul / de dessin (ms), lecture en console

function CarteCorps({slug, index}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [params, setParams] = useSearchParams();
    const jour = useMemo(aujourdhui, []);
    const marquages = useMemo(() => marquagesCarreaux(index), [index]);
    const query = params.toString();
    const vue = useMemo(() => fromQuery(query, index, jour, index.collecte), [query, index, jour]);
    const vueRef = useRef(vue);
    vueRef.current = vue;

    /** Réécrit la query string (R5). `push` pour les filtres, `replace` pour style / fond / curseur / zoom / centre. */
    const ecrire = useCallback((patch, {push = false} = {}) => {
        const suivant = {...vueRef.current, ...patch};
        vueRef.current = suivant;
        setParams(toQuery(suivant, index), {replace: !push});
    }, [index, setParams]);

    const [replie, setReplie] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const pontRef = useRef(null);

    // Curseur : `atLocal` suit le doigt / la lecture sans toucher à l'historique ; la query string est écrite 250 ms
    // après le dernier mouvement (Safari limite replaceState à 100 appels / 30 s, la lecture en fait 6 par seconde).
    const bornes = useMemo(() => bornesCurseur(index, jour), [index, jour]);
    const [atLocal, setAtLocal] = useState(null);
    const minuterieAt = useRef(null);
    const at = bornerDate(atLocal || vue.at, bornes);
    const changerAt = useCallback((valeur) => {
        setAtLocal(valeur);
        clearTimeout(minuterieAt.current);
        minuterieAt.current = setTimeout(() => ecrire({at: valeur}), 250);
    }, [ecrire]);
    // Photo et intervention du popup : calculées une fois à l'ouverture (pavId), pas à chaque rendu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const contenuSelection = useMemo(() => (selectedId == null ? null : contenuPassage(dernierPassage(index.evenementsParPav.get(selectedId)))), [selectedId]);

    // Lot 8 : ligne « Dernière collecte » de la popup (absente quand le bouton Collecte est à 0).
    const derniereCollecteSel = useMemo(
        () => (selectedId == null || !index.collecte ? undefined : derniereCollecte(index.evenementsParPav.get(selectedId), at)),
        [selectedId, index, at],
    );

    useEffect(() => { if (atLocal && vue.at === atLocal) setAtLocal(null); }, [vue.at, atLocal]);
    useEffect(() => () => clearTimeout(minuterieAt.current), []);

    // --- Données : tout vient du lot 2, mémoïsé sur des clés primitives (le permalien se réécrit à chaque déplacement) ---
    const plage = useMemo(() => ({acte: vue.acte, du: vue.du, au: vue.au, at}), [vue.acte, vue.du, vue.au, at]);
    const etats = useMemo(() => {
        const t0 = performance.now();
        const res = etatsDesPav(index, plage);
        MESURES.etatsMs = performance.now() - t0;
        return res;
    }, [index, plage]);

    const clePatrimoines = vue.patrimoines.join(',');
    const cleEtats = vue.etats.join(',');
    const filtres = useMemo(
        () => ({patrimoines: vue.patrimoines, commune: vue.commune, etats: vue.etats, element: vue.element}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clePatrimoines, vue.commune, cleEtats, vue.element],
    );
    const pavsSansEtat = useMemo(() => filtrerPavs(index, etats, {...filtres, etats: []}), [index, etats, filtres]);
    const comptesVerdict = useMemo(() => compterVerdicts(pavsSansEtat, etats), [pavsSansEtat, etats]);
    // « Tout décoché » = rien (le sélecteur du lot 2 traite une liste vide comme « pas de filtre »).
    const pavs = useMemo(() => {
        const t0 = performance.now();
        const res = vue.etats.length === 0 ? [] : filtrerPavs(index, etats, filtres);
        MESURES.filtreMs = performance.now() - t0;
        return res;
    }, [index, etats, filtres, vue.etats.length]);
    const k = useMemo(() => kpi(pavs, etats, vue.acte), [pavs, etats, vue.acte]);
    const rep = useMemo(() => repartitions(pavs, etats, index), [pavs, etats, index]);

    const groupes = useMemo(() => {
        const g = {intra: {id: 'intra', libelle: 'Intramuros', ids: [], nb: 0}, extra: {id: 'extra', libelle: 'Extramuros', ids: [], nb: 0}};
        for (const p of index.patrimoineById.values()) if (g[p.groupe]) g[p.groupe].ids.push(p.id);
        const groupeDe = new Map();
        for (const grp of Object.values(g)) grp.ids.forEach((id) => groupeDe.set(id, grp));
        for (const pav of index.pavs) {
            const grp = groupeDe.get(pav.patrimoineId);
            if (grp && pav.statut !== 'RETIRÉ') grp.nb++;
        }
        return [g.intra, g.extra].filter((x) => x.ids.length > 0);
    }, [index]);

    // Aplat par commune : part de PAV en défaut sur la sélection courante (seulement si le style est actif).
    const parts = useMemo(() => (vue.style === 'aplat' ? partsParCommune(pavs, etats) : null), [vue.style, pavs, etats]);
    const cadre = useMemo(() => index.pavs.filter((p) => p.lat != null && p.lng != null), [index]);
    // Calque circuits : seulement les circuits sélectionnés, et seulement si le calque est actif.
    const enveloppes = useMemo(() => {
        if (!vue.circuits) return null;
        const choisis = new Set(vue.patrimoines);
        const toutes = enveloppesCircuits(index);
        return {...toutes, features: toutes.features.filter((f) => choisis.has(f.properties.patrimoineId))};
    }, [index, vue.circuits, clePatrimoines]); // eslint-disable-line react-hooks/exhaustive-deps
    // Clic sur un circuit : lui seul dans le filtre ; Maj + clic l'ajoute.
    const choisirCircuit = useCallback((id, ajouter) => {
        const actuels = vueRef.current.patrimoines;
        const suite = ajouter ? [...new Set([...actuels, id])].sort((a, b) => a - b) : [id];
        ecrire({patrimoines: suite}, {push: true});
    }, [ecrire]);
    const idsRetenus = useMemo(() => new Set(pavs.map((p) => p.id)), [pavs]);
    // Chalandise (lot 12) : rayon et paliers (quantiles de la population allouée de tous les PAV de l'index, rayon courant).
    const chalandise = useMemo(
        () => (vue.style === 'chalandise'
            ? {rayonM: index.rayon, distance: libelleDistance(index.chalandise), seuils: seuilsQuantiles(index.pavs.filter((p) => p.groupe === 'extra' && p.populationAllouee != null).map((p) => p.populationAllouee))}
            : null),
        [vue.style, index],
    );

    // Causes (lot 11) : cause dominante de chaque PAV sur la plage du permalien, calculée seulement si le style est actif.
    const causes = useMemo(
        () => (vue.style === 'causes' && index.collecte ? causeDominanteParPav(index, {du: vue.du, au: vue.au}) : null),
        [vue.style, index, vue.du, vue.au],
    );

    // --- Sélection (R4) ---
    const selection = useMemo(() => {
        if (selectedId == null || !idsRetenus.has(selectedId)) return null;
        return {pav: index.pavById.get(selectedId), etat: etats.get(selectedId)};
    }, [selectedId, idsRetenus, index, etats]);

    const positionSelection = useMemo(
        () => (selection && selection.pav.lat != null ? {lat: selection.pav.lat, lng: selection.pav.lng} : null),
        [selection],
    );

    useEffect(() => {
        const surTouche = (e) => { if (e.key === 'Escape') setSelectedId(null); };
        window.addEventListener('keydown', surTouche);
        return () => window.removeEventListener('keydown', surTouche);
    }, []);

    // --- Filtres actifs, réinitialisation ---
    const defaut = useMemo(() => etatParDefaut(index, jour), [index, jour]);
    const nbFiltresActifs = (vue.patrimoines.length !== defaut.patrimoines.length ? 1 : 0)
        + (vue.commune ? 1 : 0) + (vue.etats.length !== VERDICTS.length ? 1 : 0) + (vue.element ? 1 : 0) + (vue.du ? 1 : 0) + (vue.au !== jour ? 1 : 0);

    const resume = useMemo(() => {
        const morceaux = [];
        if (vue.patrimoines.length !== defaut.patrimoines.length) {
            morceaux.push(groupes.filter((g) => g.ids.every((id) => vue.patrimoines.includes(id))).map((g) => g.libelle).join(' + ') || 'Aucun patrimoine');
        }
        morceaux.push(ACTE_LIBELLES[vue.acte]);
        if (vue.commune) morceaux.push(index.communeParCle.get(vue.commune).libelle);
        if (vue.etats.length !== VERDICTS.length) morceaux.push(vue.etats.length === 0 ? 'Aucun état' : vue.etats.map((v) => VERDICT_LIBELLES[v]).join(' / '));
        if (vue.element) morceaux.push(vue.element);
        return morceaux.join(' · ');
    }, [vue.patrimoines, vue.acte, vue.commune, vue.etats, vue.element, defaut, groupes, index]);

    const reinitialiser = () => ecrire(
        {patrimoines: defaut.patrimoines, commune: '', etats: defaut.etats, element: '', du: '', au: jour},
        {push: true},
    );
    const elargir = () => { setAtLocal(null); ecrire({du: '', au: jour, at: jour}, {push: true}); };

    // --- Actions de la barre ---
    const copierPermalien = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            toastHelper.displaySuccess('Permalien copié');
        } catch (e) {
            toastHelper.handleError(e, 'Impossible de copier le permalien.');
        }
    };
    const exporter = (format) => {
        const base = `pilotage-${slug}-carte-${at}`;
        if (format === 'csv') telecharger(`${base}.csv`, toCsv(pavs, etats, index), 'text/csv;charset=utf-8');
        else telecharger(`${base}.geojson`, JSON.stringify(toGeoJson(pavs, etats, index)), 'application/geo+json');
    };
    const filtrerVerdict = (v) => ecrire({etats: [v]}, {push: true});
    const vide = pavs.length === 0;

    return (
        <>
            <CarteBarreFiltres
                vue={vue}
                index={index}
                groupes={groupes}
                comptesVerdict={comptesVerdict}
                exportsActifs={!vide}
                nbFiltresActifs={nbFiltresActifs}
                onChange={ecrire}
                onReinitialiser={reinitialiser}
                onCopier={copierPermalien}
                onCsv={() => exporter('csv')}
                onGeoJson={() => exporter('geojson')}
            />
            <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'flex', position: 'relative', backgroundColor: '#E4E9E2'}}>
                <CartePanneau
                    replie={replie}
                    onReplier={() => setReplie((r) => !r)}
                    nbPav={pavs.length}
                    acte={vue.acte}
                    k={k}
                    parVerdict={rep.parVerdict}
                    defauts={rep.defautsParElement}
                    communes={rep.communesTouchees.map((c) => ({cle: c.cle, libelle: c.commune, nb: c.nb}))}
                    selection={selection}
                    onKpi={filtrerVerdict}
                    onElement={(libelle) => ecrire({element: libelle}, {push: true})}
                    onCommune={(cle) => ecrire({commune: cle}, {push: true})}
                    onOuvrirFiche={(pavId) => navigate(`/pilotage/${slug}/pav/${pavId}`)}
                />
                <CarteLeaflet
                    cadre={cadre}
                    vueInitiale={vue}
                    onVue={({z, c}) => ecrire({z, c})}
                    pavs={pavs}
                    etats={etats}
                    style={vue.style}
                    acte={vue.acte}
                    fond={vue.fond}
                    limites={vue.communes}
                    parts={parts}
                    circuits={enveloppes}
                    onCircuit={choisirCircuit}
                    densite={vue.densite}
                    carreaux={index.carreaux}
                    marquages={marquages}
                    chalandise={chalandise}
                    causes={causes}
                    selectedId={selection ? selection.pav.id : null}
                    onSelect={setSelectedId}
                    onMesure={mesurerDessin}
                    pontRef={pontRef}
                >
                    <CarteCurseur bornes={bornes} at={at} onChange={changerAt}/>
                    <CarteCalques
                        acte={vue.acte}
                        style={vue.style}
                        fond={vue.fond}
                        communes={vue.communes}
                        circuits={vue.circuits}
                        densite={vue.densite}
                        rayon={vue.rayon}
                        distance={libelleDistance(index.chalandise)}
                        onDensite={(densite) => ecrire({densite})}
                        onRayon={(rayon) => ecrire({rayon})}
                        onStyle={(style) => ecrire({style})}
                        onFond={(fond) => ecrire({fond})}
                        onCommunes={(communes) => ecrire({communes})}
                        onCircuits={(circuits) => ecrire({circuits})}
                    />
                    {vue.densite && <LegendeDensite carreaux={index.carreaux} marquages={marquages} bas={chalandise ? 260 : causes ? 300 : 116}/>}
                    {chalandise && <LegendeChalandise seuils={chalandise.seuils} rayonM={chalandise.rayonM} distance={chalandise.distance}/>}
                    {causes && <CarteLegendeCauses causes={causes} pavs={pavs}/>}
                    {selection && positionSelection && contenuSelection && (
                        <PopupPav key={selection.pav.id} position={positionSelection} onClose={() => setSelectedId(null)}>
                            <CartePopup
                                pav={selection.pav}
                                etat={selection.etat}
                                {...contenuSelection}
                                derniereCollecte={derniereCollecteSel}
                                cause={causes && causes.has(selection.pav.id) ? libelleCause(causes.get(selection.pav.id)) : undefined}
                                onOuvrirFiche={(pavId) => navigate(`/pilotage/${slug}/pav/${pavId}`)}
                                onPdf={(interventionId) => dispatch(synthesesActions.fetchInterventionPDF(interventionId))}
                            />
                        </PopupPav>
                    )}
                </CarteLeaflet>
                {vide && (
                    <Box sx={{position: 'absolute', inset: 0, left: replie ? 44 : 340, pointerEvents: 'none', zIndex: 3, '& > *': {pointerEvents: 'auto'}}}>
                        <EncartVide resume={resume} onReinitialiser={reinitialiser} onElargir={elargir}/>
                    </Box>
                )}
            </Box>
        </>
    );
}

export default function Carte() {
    const {slug} = useParams();
    const {data, isLoading, isError} = useGetTerritoireQuery(slug);
    // Bouton Collecte : le permalien (`collecte=`) prime s'il est présent, sinon l'état mémorisé (localStorage).
    const [params, setParams] = useSearchParams();
    const [memorise] = useCollecte();
    const brut = params.get('collecte');
    const collecte = brut === '1' ? true : brut === '0' ? false : memorise;
    useEffect(() => { if (brut !== null && collecte !== memorise) ecrireCollecte(collecte); }, [brut, collecte, memorise]);
    const changerCollecte = useCallback((valeur) => {
        ecrireCollecte(valeur);
        if (params.has('collecte')) {
            const suivant = new URLSearchParams(params);
            suivant.set('collecte', valeur ? '1' : '0');
            setParams(suivant, {replace: true});
        }
    }, [params, setParams]);
    // Lot 12 : le rayon de population allouée (permalien `rayon=`) change l'index (populationAllouee des PAV).
    const rayonBrut = Number(params.get('rayon'));
    const rayon = RAYONS.includes(rayonBrut) ? rayonBrut : RAYON_DEFAUT;
    const index = useMemo(() => (data ? indexer(data, {collecte, rayon}) : null), [data, collecte, rayon]);

    let corps;
    if (isLoading) {
        corps = (
            <>
                <CarteBarreFiltres
                    inerte
                    vue={{patrimoines: [], acte: 'maint', du: '', au: '', commune: '', etats: VERDICTS, element: '', style: 'abribacs', fond: 'plan', communes: true, circuits: false, collecte: false, rayon: RAYON_DEFAUT, densite: false, at: '', z: null, c: null}}
                    index={null}
                    groupes={[{id: 'intra', libelle: 'Intramuros', ids: [], nb: null}, {id: 'extra', libelle: 'Extramuros', ids: [], nb: null}]}
                    comptesVerdict={null}
                    exportsActifs={false}
                    nbFiltresActifs={0}
                />
                <Box sx={{flex: '1 1 auto', minHeight: 0, display: 'flex', position: 'relative', backgroundColor: '#E4E9E2'}}>
                    <CartePanneau chargement replie={false} onReplier={() => {}}/>
                    <Box sx={{flex: '1 1 auto', position: 'relative', backgroundColor: '#E4E9E2'}}>
                        <EncartChargement/>
                    </Box>
                </Box>
            </>
        );
    } else if (isError || !index) {
        corps = (
            <Box sx={{p: '24px'}}>
                <Typography component="h1" sx={{fontSize: 24, fontWeight: 800, mb: '16px'}}>Carte</Typography>
                <NoDataLabel label="Territoire indisponible"/>
            </Box>
        );
    } else {
        corps = <CarteCorps slug={slug} index={index}/>;
    }

    return (
        <Coquille slug={slug} onglet="carte" index={index} pleinEcran collecte={collecte} onCollecte={changerCollecte}>
            <Box sx={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontSize: 14, lineHeight: 1.45}}>{corps}</Box>
        </Coquille>
    );
}
